import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'

import { selectSessionFilter, setSessionFilterIsLoading, setSessionQuickFilter } from '../../store/sessionFilter/sessionFilter'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetSessionFiltersQuery } from '../../store/sessionFilter/quickFilter'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

import { OverflowTooltip } from '../../apps/professional-app/fa/common/OverflowTooltip/OverflowTooltip'

import { useStickySearchParams } from '../../common/useStickySearchParams'

import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../SelectDropdown/SelectDropdown'
import { Button } from '../Button/Button'
import { TagElement } from '../Tag/Tag'

import { SessionFilterViewProps, STICKY_SESSION_FILTER_SEARCH_PARAMS } from './model'

interface SessionQuickFilterProps extends SessionFilterViewProps {
  onEditClicked: (filterId: string) => void
  onNewFilterClicked: () => void
}

export const SessionQuickFilter: React.FC<SessionQuickFilterProps> = ({ disabled = false, onEditClicked, onNewFilterClicked, filterType, onFilterChanged }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const selectedQuickFilter = useAppSelector(selectSessionFilter)

  const { searchParams, setSearchParams } = useStickySearchParams(STICKY_SESSION_FILTER_SEARCH_PARAMS)

  const { data } = useGetSessionFiltersQuery(
    { accountId, userId: currentUserId, filterType: filterType, pageRequest: { page: 0, size: 500 } },
    { refetchOnMountOrArgChange: true },
  )

  const quickFilters = data?.data

  const quickFilterOptions: SelectOption[] = useMemo(() => quickFilters?.map(({ id, name }) => ({ id, label: name })) ?? [], [quickFilters])

  useEffect(() => {
    if (quickFilters == null) {
      return
    }
    dispatch(setSessionFilterIsLoading(false))

    const [filterId, userId, meetingKind, meetingSources, dateRange, meetingTemplates, tags] = [
      searchParams.get('filterId'),
      searchParams.get('userId'),
      searchParams.get('meetingKind'),
      searchParams.get('meetingSources'),
      searchParams.get('dateRange'),
      searchParams.get('meetingTemplates'),
      searchParams.get('tags'),
    ]

    if (filterId) {
      const quickFilter = quickFilters.find((filter) => filter.id === filterId)
      if (quickFilter) {
        dispatch(setSessionQuickFilter(quickFilter))
      }
    } else {
      if (!userId && !meetingKind && !meetingSources && !dateRange && !meetingTemplates && !tags) {
        const defaultQuickFilter = quickFilters.find((filter) => filter.isDefault)
        if (defaultQuickFilter) {
          dispatch(setSessionQuickFilter(defaultQuickFilter))
          searchParams.set('filterId', defaultQuickFilter.id)
          onFilterChanged?.(searchParams)
        }
      }
    }
  }, [dispatch, onFilterChanged, quickFilters, searchParams])

  const handleQuickFilterSelected = (option: SelectOption) => {
    if (quickFilters == null) {
      return
    }
    const quickFilter = quickFilters.find((filter) => filter.id === option.id)
    if (!quickFilter) {
      return
    }
    dispatch(setSessionQuickFilter(quickFilter))
    const newSearchParams = new URLSearchParams()
    newSearchParams.set('filterId', quickFilter.id)
    setSearchParams(newSearchParams, { replace: true })
  }

  const handleQuickFilterEdit = (optionId: string) => {
    onEditClicked(optionId)
  }

  const QuickFilterItemOption: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, onClick }) => {
    const quickFilter = quickFilters?.find((filter) => filter.id === optionId)
    return (
      <CustomDropdownItemOption role='button' onClick={onClick}>
        <div className='d-flex align-items-center gap-2'>
          <OverflowTooltip>{optionLabel}</OverflowTooltip>
          {quickFilter && quickFilter.isDefault ? <span className='zocks-text-primary lead-extra-small'>Default</span> : null}
          {quickFilter ? (
            <>
              <TagElement
                className='lead-extra-small zocks-py-0.125 px-1 text-uppercase'
                style={{ backgroundColor: quickFilter.type === 'system' ? '#C29FFA' : '#000', color: '#fff' }}
              >
                {quickFilter.type === 'system' ? 'zocks' : 'custom'}
              </TagElement>
              {quickFilter.type === 'user' ? (
                <div className='quick-filter-item-option__edit-button' onClick={() => handleQuickFilterEdit(optionId)}>
                  <i className='bi bi-pencil' />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </CustomDropdownItemOption>
    )
  }

  const CreateNewDropdownFooter = () => {
    return (
      <Button className='quick-filter-dropdown__create-new-button mt-3 button-medium w-100' variant='dashedButton' onClick={onNewFilterClicked}>
        + Create new
      </Button>
    )
  }

  return (
    <div className='quick-filter-dropdown-container'>
      <div className='lead-extra-small mb-1 d-flex gap-1'>
        {selectedQuickFilter ? (
          <>
            <i className='bi bi-stopwatch' />
            Quick filter
          </>
        ) : null}
      </div>
      <SelectDropdown
        className={classNames('py-2 zocks-px-0.75', { 'quick-filter-dropdown--unselected': !selectedQuickFilter })}
        disabled={disabled}
        options={quickFilterOptions}
        onOptionSelected={handleQuickFilterSelected}
        initialSelectedOption={quickFilterOptions.find((option) => option.id === selectedQuickFilter?.id) ?? null}
        DropDownItemOptionComponent={QuickFilterItemOption}
        DropdownFooterComponent={CreateNewDropdownFooter}
        placeholder='Quick filter'
      />
    </div>
  )
}
